import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Button, Form } from 'react-bootstrap';
import { DateRange } from 'react-date-range';
import DonationTable from './DonationTable';
import TransactionTable from './TransactionTable';
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Calendar from '../../assets/images/svg/Calendar_SoS.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const HistoryTabs = () => {
    // Tab State
    const [activeTab, setActiveTab] = useState('donation');

    // UI States
    const [showSearch, setShowSearch] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    // Filter States
    const [searchValue, setSearchValue] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [dateRange, setDateRange] = useState([
        {
            startDate: null,
            endDate: null,
            key: 'selection'
        }
    ]);

    // Refs for click outside handling
    const searchRef = useRef(null);
    const calendarRef = useRef(null);
    const dropdownRef = useRef(null);

    const filterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Success', value: 'SUCCESS' }
    ];

    // Handle filter changes
    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        setShowDropdown(false);
    };
    // Handle date range changes
    const handleDateRangeChange = (item) => {
        setDateRange([item.selection]);
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchValue(value);
    };

    // Clear date filter
    const clearDateFilter = () => {
        setDateRange([{
            startDate: null,
            endDate: null,
            key: 'selection'
        }]);
        setShowCalendar(false);
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearch(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Container fluid={true}>
            {/* Header with Title and Icons */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Donation</h5>

                <div className="d-flex gap-1">
                    {/* Search Icon */}
                    <div className="position-relative" ref={searchRef}>
                        <button
                            className="bg-transparent border-0"
                            onClick={() => setShowSearch(true)}
                        >
                            <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
                        </button>

                        {showSearch && (
                            <div className="position-absolute end-0 mt-2" style={{ width: '250px', zIndex: 1000, right: '-180px', top: '45px' }}>
                                <div className="bg-white rounded shadow p-3">
                                    <div className="d-flex align-items-center bg-light rounded p-2">
                                        <input
                                            type="text"
                                            placeholder="Search Here..."
                                            value={searchValue}
                                            onChange={(e) => handleSearch(e.target.value)}
                                            className="border-0 bg-transparent flex-grow-1"
                                            style={{ outline: 'none' }}
                                            autoFocus
                                        />
                                        <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Calendar Icon */}
                    <div className="position-relative" ref={calendarRef}>
                        <button
                            className="bg-transparent border-0"
                            onClick={() => setShowCalendar(!showCalendar)}
                        >
                            <img src={Calendar} alt="calendar" style={{ width: '36px', height: '36px' }} />
                        </button>

                        {showCalendar && (
                            <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-3" style={{ zIndex: 1000 }}>
                                <DateRange
                                    editableDateInputs={true}
                                    onChange={handleDateRangeChange}
                                    moveRangeOnFirstSelection={false}
                                    ranges={dateRange}
                                    maxDate={new Date()}
                                    className="date-range"
                                />
                                <div className="d-flex justify-content-end mt-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearDateFilter}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Filter Icon */}
                    <div className="position-relative" ref={dropdownRef}>
                        <button
                            className="bg-transparent border-0"
                            onClick={() => setShowDropdown(!showDropdown)}
                        >
                            <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
                        </button>

                        {showDropdown && (
                            <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2" style={{ width: '200px', zIndex: 1000 }}>
                                <Form>
                                    {filterOptions.map((option) => (
                                        <Form.Check
                                            key={option.value}
                                            type="checkbox"
                                            name="filterOption"
                                            id={`filter-${option.value}`}
                                            label={option.label}
                                            checked={selectedFilter === option.value}
                                            onChange={() => handleFilterChange(option.value)}
                                        />
                                    ))}
                                </Form>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="d-flex mb-0 gap-2" >
                <button
                    className={`btn py-3 px-4 rounded-2 ${activeTab === 'donation'
                            ? 'text-white'
                            : 'text-gray-600'
                        }`}
                    onClick={() => setActiveTab('donation')}
                    style={{
                        backgroundColor: activeTab === 'donation' ? '#5C61F2' : '#F8F9FA',
                        borderRight: '1px solid #5C61F2',
                        borderTop: '1px solid #5C61F2',
                        borderLeft: '1px solid #5C61F2',
                        // borderBottom: activeTab === 'donation' ? 'none' : '1px solid #5C61F2',
                        minWidth: '200px',
                        marginBottom: '-1px',
                        fontWeight: 500
                    }}
                >
                    Donation History
                </button>
                <button
                    className={`btn py-3 px-4 rounded-2 ${activeTab === 'transaction'
                            ? 'text-white'
                            : 'text-gray-600'
                        }`}
                    onClick={() => setActiveTab('transaction')}
                    style={{
                        backgroundColor: activeTab === 'transaction' ? '#5C61F2' : '#F8F9FA',
                        borderRight: '1px solid #5C61F2',
                        borderTop: '1px solid #5C61F2',
                        borderLeft: '1px solid #5C61F2',
                        // borderBottom: activeTab === 'transaction' ? 'none' : '1px solid #5C61F2',
                        minWidth: '200px',
                        marginBottom: '-1px',
                        fontWeight: 500
                    }}
                >
                    Transaction History
                </button>
            </div>

            {/* Table Content */}
            <Row>
                <Col sm="12">
                    {activeTab === 'donation' ? (
                        <DonationTable
                            searchValue={searchValue}
                            selectedFilter={selectedFilter}
                            dateRange={dateRange[0]}
                        />
                    ) : (
                        <TransactionTable
                            searchValue={searchValue}
                            selectedFilter={selectedFilter}
                            dateRange={dateRange[0]}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default HistoryTabs;